import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const WEBHOOK_URL = `${process.env.REACT_APP_BACKEND_URL}/api/printify/webhook`;

export default function PrintifyPanel({ onSynced }) {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [registering, setRegistering] = useState(false);

  const load = () => api.get("/admin/printify/status", { params: { webhook_url: WEBHOOK_URL } }).then(({ data }) => setStatus(data)).catch(() => setStatus(null));
  useEffect(() => { load(); }, []);

  const sync = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/admin/products/sync-printify"); toast.success(data.message || `Synced ${data.synced}`); load(); onSynced?.(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSyncing(false); }
  };
  const register = async () => {
    setRegistering(true);
    try { await api.post("/admin/printify/register-webhook", { url: WEBHOOK_URL }); toast.success("Printify will now send order & shipping updates 📦"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRegistering(false); }
  };

  if (!status) return null;
  return (
    <div className="mb-4" data-testid="printify-panel">
      <div className="flex gap-3 flex-wrap items-center">
        <button onClick={sync} disabled={syncing || !status.connected} className="btn-outline !py-2.5" data-testid="admin-sync-printify">{syncing ? "Syncing…" : "Sync from Printify"}</button>
        <span data-testid="printify-status" className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold ${status.connected ? "bg-[#A78BFA]/20 text-[#19324A]" : "bg-[#FF6B9D]/15 text-[#FF6B9D]"}`}>
          <span className={`w-2 h-2 rounded-full ${status.connected ? "bg-[#A78BFA]" : "bg-[#FF6B9D]"}`} />
          Printify {status.connected ? `connected${typeof status.linked_products === "number" ? ` · ${status.linked_products} linked` : ""}` : "not connected"}
        </span>
      </div>
      {status.message && <p className="text-xs text-[#19324A]/50 mt-2" data-testid="printify-message">{status.message} New products publish straight away with your Printify prices; re-syncs nightly at 3am.{status.last_sync ? ` Last sync: ${new Date(status.last_sync).toLocaleString("en-AU")}${status.last_sync_error ? ` (failed: ${status.last_sync_error})` : ""}.` : ""}</p>}
      {status.connected && status.shops > 0 && (
        <div className="flex items-center gap-2 flex-wrap mt-2 text-xs text-[#19324A]/70">
          <span className="font-semibold">Order & shipping updates webhook:</span>
          {status.webhook_registered ? (
            <span className="px-2 py-1 rounded-full bg-[#7ED957]/20 font-bold text-[#19324A]" data-testid="printify-webhook-ok">Registered ✓</span>
          ) : (
            <button type="button" onClick={register} disabled={registering} className="font-bold text-[#A78BFA]" data-testid="printify-register-webhook">{registering ? "Registering…" : "Register with Printify"}</button>
          )}
          <code className="px-2 py-1 rounded-lg bg-[#19324A]/5 break-all" data-testid="printify-webhook-url">{WEBHOOK_URL}</code>
        </div>
      )}
    </div>
  );
}
